import React, { useContext } from 'react';
import useUser from '../../../client/contexts/UserContext';
import { SearchState } from '@reducers/search';
import { LoginTemplate, PublicUser } from '@sbt-web/auth';
import {
  HADES_PATH,
  MOBILE_API_CHANNEL,
  WEB_API_CHANNEL,
} from '@shared/constants';
import { sendNotification } from '@shared/tools/Notifications';
import notificationClasses from './notification.module.scss';
import { mapToSavedSearch } from './stateTranslator';
import { DefaultTrackingEvents, SavedSearchesEvents } from './trackingEvents';
import { getUserSavedSearch, saveSearch } from '@sbt-web/network/client';
import { HTTPStatusCode, NetError, SavedSearch } from '@sbt-web/network/types';
import { captureException } from '@sentry/nextjs';
import { isMobileViewport } from '@shared/helpers/Screen';
import { FiltersConfigState } from '@reducers/filtersConfig';
import { OptimizelySubitoContext } from '@sbt-web/houston-wrapper';

const NotificationSaveSearch = () => {
  return (
    <p className="body-text">
      Verrai avvisato sui nuovi annunci.{' '}
      <br className={notificationClasses['message-line-break']} />
      Gestisci le tue{' '}
      <a
        className={notificationClasses.link}
        href={`${process.env.NEXT_PUBLIC_AREARISERVATA_BASE_URL}/ricerche`}
      >
        ricerche salvate
      </a>
    </p>
  );
};

export interface SaveSearchProvider {
  saveSearch(
    search: SearchState,
    filtersConfig: FiltersConfigState
  ): Promise<void>;
  getSavedSearches(): Promise<Array<SavedSearch>>;
}

export function useSavedSearches(
  savedSearchesEvents: SavedSearchesEvents = new DefaultTrackingEvents()
): SaveSearchProvider {
  const { user, requestLogin } = useUser();
  const { optimizely } = useContext(OptimizelySubitoContext);

  function requestLoginThenSave(
    search: SearchState,
    filtersConfig: FiltersConfigState
  ): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      requestLogin(LoginTemplate.MySearches, (user) => {
        savedSearchesEvents.onLoginInPlace(search, user);

        privateSaveSearch(search, user, filtersConfig).then(resolve, reject);
      });
    });
  }

  async function privateGetSavedSearches(
    user: PublicUser
  ): Promise<Array<SavedSearch>> {
    try {
      const { body } = await getUserSavedSearch(HADES_PATH, user.id);
      return body.savedSearches;
    } catch {
      return [];
    }
  }

  async function privateSaveSearch(
    search: SearchState,
    user: PublicUser,
    filtersConfig: FiltersConfigState
  ): Promise<void> {
    try {
      const channel = isMobileViewport() ? MOBILE_API_CHANNEL : WEB_API_CHANNEL;
      const { body } = await saveSearch(
        HADES_PATH,
        user.id,
        mapToSavedSearch(search, filtersConfig),
        channel
      );

      optimizely?.track('save_search_click', user.id);
      savedSearchesEvents.onSave(search, body);
      sendNotification(<NotificationSaveSearch />, 'success');
      window.dispatchEvent(new Event('subito:saveSearch:saved'));
    } catch (e) {
      if (
        e instanceof NetError &&
        e.cause.status === HTTPStatusCode.Unauthorized
      ) {
        return requestLoginThenSave(search, filtersConfig);
      }

      sendNotification('Impossibile salvare la ricerca', 'error');

      captureException(new Error(`Unable to save the search due to: ${e}`));
    }
  }

  return {
    getSavedSearches: async () => {
      if (user === null) {
        return [];
      } else {
        return await privateGetSavedSearches(user);
      }
    },

    saveSearch: async (search, filtersConfig): Promise<void> => {
      if (!search) {
        throw new Error('Missing required parameters');
      }

      savedSearchesEvents.onBeforeSave(search);

      if (user === null) {
        await requestLoginThenSave(search, filtersConfig);
      } else {
        await privateSaveSearch(search, user, filtersConfig);
      }
    },
  };
}
